import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysParsingCase } from '../queryKeys';

import { fetchParsingCases } from './fetchParsingCases';
import {
  type ListParsingCasesParams,
  type PaginatedParsingCasesResponse,
} from './types';

/**
 * Generates the query options for fetching a paginated list of parsing cases.
 *
 * @param params - Filter and pagination parameters
 * @param options - Additional options for the query
 * @returns The query options including queryKey, queryFn, and other settings
 */
export const getParsingCasesQueryOptions = <
  TData = PaginatedParsingCasesResponse,
>({
  params,
  options,
}: {
  params: ListParsingCasesParams;
  options?: Omit<
    UseQueryOptions<PaginatedParsingCasesResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchParsingCases(params, { signal }),
    queryKey: queryKeysParsingCase.getAll(params),
    // Retried cases stay IN_PROGRESS until the backend job finishes; poll so
    // their status chips resolve without a manual refresh.
    refetchInterval(query) {
      return query.state.data?.data.some(
        ({ status }) => status === 'IN_PROGRESS',
      )
        ? ms('5s')
        : false;
    },
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query hook for fetching a paginated list of DNPv2 parsing cases (vadmin only).
 *
 * @param params - Filter and pagination parameters
 * @param options - Additional React Query options
 * @returns Query result containing paginated parsing cases
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/list_parsing_cases_vadmin_parsing_cases_get
 */
export const useQueryParsingCases = <TData = PaginatedParsingCasesResponse>(
  params: ListParsingCasesParams,
  options?: Omit<
    UseQueryOptions<PaginatedParsingCasesResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getParsingCasesQueryOptions<TData>({ params, options }));
